import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { Link, useNavigate } from 'react-router-dom';
import { useFieldAuth } from '../contexts/FieldContext';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import { getFieldsDictionary } from '../services/fieldService';
import { FieldDictionary } from '../types/field';
import FieldSelector from '../components/common/FieldSelector';
import StateSelector from '../components/common/StateSelector';
import AnimatedBackground from '../components/common/AnimatedBackground';
import PasswordField from '../components/common/PasswordField';
import { formatPhoneNumber } from '../utils/validation';

const Background = styled.div`
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  
  /* Desktop - fixed height, no scroll */
  @media (min-width: 769px) {
    height: 100vh;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }
  
  /* Mobile - scrollable */
  @media (max-width: 768px) {
    min-height: 100vh;
    overflow: visible;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({ theme }) =>
      theme.profile?.pageBackground === 'transparent'
        ? 'none'
        : "url('data:image/svg+xml,%3Csvg width=\\'2\\' height=\\'2\\' xmlns=\\'http://www.w3.org/2000/svg\\'%3E%3Ccircle cx=\\'1\\' cy=\\'1\\' r=\\'0.2\\' fill=\\'#003300\\' fill-opacity=\\'0.25\\'/%3E%3C/svg%3E')"};
    pointer-events: none;
  }
`;


const CenterBlock = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Desktop - centered, fixed position */
  @media (min-width: 769px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    justify-content: center;
    padding: 15px;
    overflow: hidden;
  }
  
  /* Mobile - scrollable from top */
  @media (max-width: 768px) {
    position: relative;
    justify-content: flex-start;
    min-height: 100vh;
    padding: 80px 15px 20px; /* Top padding to avoid header */
  }
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 255, 102, 0.3);
  padding: 25px;
  border-radius: 12px;
  max-width: 550px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    max-width: 420px;
    padding: 20px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.7rem;
  color: ${({ theme }) => theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 12px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 255, 102, 0.5);
`;

const SubStatus = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin-bottom: 15px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const InputBlock = styled.div`
  margin-bottom: 12px;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  letter-spacing: 1px;
  margin-bottom: 5px;
  font-weight: 500;
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px rgba(0, 255, 102, 0.2);
  }

  &::placeholder {
    color: #888;
    opacity: 0.7;
  }
`;

const SelectField = styled.select`
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px rgba(0, 255, 102, 0.2);
  }

  option {
    background: #0a1a0a;
    color: #fff;
  }
`;

const TextAreaField = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 102, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 65px;
  resize: vertical;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff66;
    box-shadow: 0 0 10px rgba(0, 255, 102, 0.2);
  }

  &::placeholder {
    color: #888;
    opacity: 0.7;
  }
`;

const CheckboxBlock = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  text-align: left;
`;

const CheckboxInput = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  color: #b0b0b0;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00ff66, #00cc52);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 10px 0;
  cursor: pointer;
  margin: 12px 0;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #00cc52, #009940);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BottomLink = styled.div`
  color: #b0b0b0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  
  a {
    color: #00ff66;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #fff;
    }
  }
`;

const ErrorText = styled.div`
  color: #ff6666;
  font-size: 10px;
  margin-top: 5px;
  text-align: left;
`;

// Стили для модального окна успешной регистрации
const InfoOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 20, 0, 0.9);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InfoBox = styled.div`
  background: rgba(0, 30, 0, 0.9);
  border: 2px solid #66ff66;
  padding: 25px;
  max-width: 320px;
  width: 90%;
  position: relative;
`;

const InfoTitle = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 15px;
  text-align: center;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  text-transform: uppercase;
  &::before, &::after {
    content: '//';
    color: #66ff66;
    margin: 0 5px;
  }
`;

const InfoText = styled.p`
  color: #fff;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 15px;
  font-family: 'Courier New', monospace;
  text-align: center;
`;

const InfoButton = styled.button`
  background: linear-gradient(135deg, #00ff66, #00cc52);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 24px;
  cursor: pointer;
  width: 100%;
  margin-top: 15px;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #00cc52, #009940);
    transform: translateY(-2px);
  }
`;

const CustomFieldsBlock = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed rgba(0, 212, 255, 0.5);
  background: rgba(0, 212, 255, 0.05);
  border-radius: 6px;
`;

// Стили для модального окна предупреждения
const WarningOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(20, 0, 0, 0.95);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WarningModal = styled.div`
  background: rgba(30, 0, 0, 0.95);
  border: 3px solid #ff4444;
  box-shadow: 0 0 30px rgba(255, 68, 68, 0.5);
  padding: 30px;
  max-width: 500px;
  width: 90%;
  position: relative;
  font-family: 'Courier New', monospace;
`;

const WarningTitle = styled.h2`
  color: #ff4444;
  font-size: 1.4rem;
  margin-bottom: 20px;
  text-align: center;
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
  &::before, &::after {
    content: '⚠️';
    margin: 0 10px;
  }
`;

const WarningText = styled.div`
  color: #fff;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 25px;
  font-family: 'Courier New', monospace;
  
  p {
    margin-bottom: 15px;
  }
  
  strong {
    color: #ff6666;
    font-weight: bold;
  }
`;

const WarningButtonsContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
`;

const WarningButton = styled.button<{ $variant: 'confirm' | 'cancel' }>`
  padding: 12px 25px;
  border: 2px solid ${props => props.$variant === 'confirm' ? '#ff4444' : '#666'};
  background: ${props => props.$variant === 'confirm' ? 'rgba(255, 68, 68, 0.2)' : 'rgba(102, 102, 102, 0.2)'};
  color: ${props => props.$variant === 'confirm' ? '#ff6666' : '#ccc'};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  border-radius: 0;
  transition: all 0.3s ease;
  min-width: 140px;

  &:hover {
    background: ${props => props.$variant === 'confirm' ? 'rgba(255, 68, 68, 0.4)' : 'rgba(102, 102, 102, 0.4)'};
    box-shadow: 0 0 15px ${props => props.$variant === 'confirm' ? 'rgba(255, 68, 68, 0.3)' : 'rgba(102, 102, 102, 0.3)'};
  }
`;

const RegisterFieldPage: React.FC = () => {
  const { registerField, loading } = useFieldAuth();
  const [passwordValid, setPasswordValid] = useState(false);
  const navigate = useNavigate();
  const { notifications, showNotification, removeNotification } = useNotification();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fieldDictionaryId: '',
    isCustomField: false,
    customFieldName: '',
    customAddress: '',
    state: 'Florida',
    phone: '',
    description: '',
    website: '',
    supportedGameTypes: [] as string[]
  });
  
  const [formattedPhone, setFormattedPhone] = useState('');

  const [fieldsDictionary, setFieldsDictionary] = useState<FieldDictionary[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Запрещаем редактирование "+1 " в начале, если оно есть
    if (inputValue.startsWith('+1') && !formData.phone.startsWith('+1')) {
      setFormData(prev => ({ ...prev, phone: '+1 ' }));
      setFormattedPhone('+1 ');
      return;
    }

    // Ограничиваем максимальную длину
    if (inputValue.length > 17) {
      return;
    }

    // Если пользователь пытается удалить что-то из "+1 ", восстанавливаем минимум
    if (formData.phone.startsWith('+1') && inputValue.length < 3) {
      setFormData(prev => ({ ...prev, phone: '+1 ' }));
      setFormattedPhone('+1 ');
    } else {
      setFormData(prev => ({ ...prev, phone: inputValue }));
      setFormattedPhone(formatPhoneNumber(inputValue));
    }
  };

  // Форматируем телефон при вводе
  useEffect(() => {
    setFormattedPhone(formatPhoneNumber(formData.phone));
  }, [formData.phone]);

  useEffect(() => {
    const fetchFieldsDictionary = async () => {
      try {
        const fields = await getFieldsDictionary();
        setFieldsDictionary(fields);
      } catch (error) {
        console.error('Error fetching fields dictionary:', error);
      } finally {
        setLoadingFields(false);
      }
    };

    fetchFieldsDictionary();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      
      // Очищаем поля при переключении checkbox
      if (name === 'isCustomField') {
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          fieldDictionaryId: checked ? '' : prev.fieldDictionaryId,
          customFieldName: checked ? prev.customFieldName : '',
          customAddress: checked ? prev.customAddress : ''
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Очищаем ошибку для этого поля
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Обработчик для очистки ошибки при изменении fieldDictionaryId через FieldSelector
  const handleFieldDictionaryChange = (fieldId: string) => {
    // Находим выбранное поле и берем его штат
    const selectedField = fieldsDictionary.find(field => field.id === fieldId);
    const selectedState = selectedField ? selectedField.state : 'Florida';
    
    setFormData(prev => ({ 
      ...prev, 
      fieldDictionaryId: fieldId,
      state: selectedState
    }));
    
    if (errors.fieldDictionaryId) {
      setErrors(prev => ({ ...prev, fieldDictionaryId: '' }));
    }
    if (errors.state) {
      setErrors(prev => ({ ...prev, state: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!passwordValid) newErrors.password = 'Password does not meet requirements';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.state) newErrors.state = 'State is required';

    if (formData.isCustomField) {
      if (!formData.customFieldName) newErrors.customFieldName = 'Field name is required';
      if (!formData.customAddress) newErrors.customAddress = 'Address is required';
      if (!formData.supportedGameTypes.length) newErrors.supportedGameTypes = 'At least one game type must be selected';
    } else {
      if (!formData.fieldDictionaryId) newErrors.fieldDictionaryId = 'Please select a field';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Показываем предупреждающее модальное окно
    setShowWarningModal(true);
  };

  const handleConfirmRegistration = async () => {
    setShowWarningModal(false);
    
    try {
      // Отправляем данные с очищенным номером телефона
      const cleanPhone = formData.phone.replace(/\D/g, '');
      await registerField({ ...formData, phone: cleanPhone });
      // После регистрации показываем сообщение о необходимости подтверждения email
      setRegistrationComplete(true);
    } catch (error: any) {
      showNotification('error', 'Registration Failed', error.message || 'Registration failed');
    }
  };

  const handleCancelRegistration = () => {
    setShowWarningModal(false);
  };

  // Модальное окно предупреждения
  if (showWarningModal) {
    return (
      <WarningOverlay>
        <WarningModal>
          <WarningTitle>CRITICAL WARNING</WarningTitle>
          
          <WarningText>
            <p>
              <strong>Field registration is for field owners only!</strong>
            </p>
            
            <p>
              If you are a regular player, please register as a <strong>PLAYER</strong> instead.
            </p>
            
            <p>
              <strong>IMPORTANT:</strong> By proceeding, you confirm that you are the legitimate owner of this field. 
              Fraudulent registration using another field's name without authorization is strictly prohibited.
            </p>
            
            <p>
              <strong>WARNING:</strong> If you register using someone else's field name without permission, 
              and the real field owner later contacts us to register their field, we will:
            </p>
            
            <p style={{ color: '#ff6666', fontWeight: 'bold' }}>
              • Verify the legitimate owner's identity<br/>
              • Permanently delete your fraudulent account<br/>
              • Ban your email and phone number from our platform forever<br/>
              • Take legal action if necessary
            </p>
            
            <p>
              Only proceed if you are the <strong>LEGITIMATE FIELD OWNER</strong> and have the right to register this field.
            </p>
          </WarningText>
          
          <WarningButtonsContainer>
            <WarningButton $variant="cancel" onClick={handleCancelRegistration}>
              CANCEL
            </WarningButton>
            <WarningButton $variant="confirm" onClick={handleConfirmRegistration}>
              I UNDERSTAND & AGREE
            </WarningButton>
          </WarningButtonsContainer>
        </WarningModal>
      </WarningOverlay>
    );
  }

  // Модальное окно после успешной регистрации
  if (registrationComplete) {
    return (
      <InfoOverlay>
        <InfoBox>
          <InfoTitle>Registration Successful</InfoTitle>
          
          <InfoText>
            Please check your email ({formData.email}) to verify your account.
          </InfoText>
          
          <InfoText>
            A verification link has been sent to your email. Click the link to complete your registration.
          </InfoText>
          
          <InfoText>
            <strong style={{ color: '#66ff66' }}>IMPORTANT:</strong> The verification link will expire in 24 hours.
          </InfoText>
          
          <InfoButton onClick={() => navigate('/')}>
            Return to Home
          </InfoButton>
        </InfoBox>
      </InfoOverlay>
    );
  }

  return (
    <Background>
      <CenterBlock>
        <AccessFrame>
          <AccessTitle>Field Registration</AccessTitle>
          <SubStatus>Register your field in the NovaWarfare network</SubStatus>

          <form onSubmit={handleSubmit}>
            <InputBlock>
              <Label>Email Address</Label>
              <InputField
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="field@example.com"
                autoComplete="email"
              />
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
            </InputBlock>

            <InputBlock>
              <PasswordField
                label="Password"
                value={formData.password}
                onChange={(v)=>handleInputChange({target:{name:'password',value:v}} as any)}
                withConfirm
                confirmValue={formData.confirmPassword}
                onConfirmChange={(v)=>handleInputChange({target:{name:'confirmPassword',value:v}} as any)}
                emailForChecks={formData.email}
                autoComplete="new-password"
                onValidityChange={(ok)=>setPasswordValid(ok)}
                showInlineChecklist={false}
                showHelp
              />
              {errors.password && <ErrorText>{errors.password}</ErrorText>}
            </InputBlock>

            <CheckboxBlock>
              <CheckboxInput
                type="checkbox"
                name="isCustomField"
                checked={formData.isCustomField}
                onChange={handleInputChange}
              />
              <CheckboxLabel>My field is not in the list</CheckboxLabel>
            </CheckboxBlock>

            {!formData.isCustomField ? (
              <InputBlock>
                <Label>Select Your Field</Label>
                <FieldSelector
                  value={formData.fieldDictionaryId}
                  onChange={handleFieldDictionaryChange}
                  fields={fieldsDictionary}
                  placeholder="Search and select your field..."
                  loading={loadingFields}
                  required
                />
                {errors.fieldDictionaryId && <ErrorText>{errors.fieldDictionaryId}</ErrorText>}
              </InputBlock>
            ) : (
              <CustomFieldsBlock>
                <InputBlock>
                  <Label>Field Name</Label>
                  <InputField
                    type="text"
                    name="customFieldName"
                    value={formData.customFieldName}
                    onChange={handleInputChange}
                    placeholder="Enter field name"
                  />
                  {errors.customFieldName && <ErrorText>{errors.customFieldName}</ErrorText>}
                </InputBlock>

                <InputBlock>
                  <Label>Field Address</Label>
                  <InputField
                    type="text"
                    name="customAddress"
                    value={formData.customAddress}
                    onChange={handleInputChange}
                    placeholder="Enter field address"
                  />
                  {errors.customAddress && <ErrorText>{errors.customAddress}</ErrorText>}
                </InputBlock>

                <InputBlock>
                  <Label>State</Label>
                  <StateSelector
                    value={formData.state}
                    onChange={(state) => setFormData(prev => ({ ...prev, state }))}
                    placeholder="Select state..."
                    required
                  />
                  {errors.state && <ErrorText>{errors.state}</ErrorText>}
                </InputBlock>

                <InputBlock>
                  <Label>Supported Game Types</Label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '5px' }}>
                    {['airsoft', 'paintball', 'speedball'].map(gameType => (
                      <CheckboxBlock key={gameType} style={{ margin: 0 }}>
                        <CheckboxInput
                          type="checkbox"
                          checked={formData.supportedGameTypes.includes(gameType)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              supportedGameTypes: isChecked
                                ? [...prev.supportedGameTypes, gameType]
                                : prev.supportedGameTypes.filter(type => type !== gameType)
                            }));
                          }}
                        />
                        <CheckboxLabel style={{ textTransform: 'capitalize' }}>
                          {gameType}
                        </CheckboxLabel>
                      </CheckboxBlock>
                    ))}
                  </div>
                  {errors.supportedGameTypes && <ErrorText>{errors.supportedGameTypes}</ErrorText>}
                </InputBlock>
              </CustomFieldsBlock>
            )}


            <InputBlock>
              <Label>Phone Number</Label>
              <InputField
                type="tel"
                name="phone"
                value={formattedPhone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                inputMode="tel"
                maxLength={17}
              />
              {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
            </InputBlock>

            <InputBlock>
              <Label>Field Description</Label>
              <TextAreaField
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your tactical field, facilities, and game types..."
              />
              {errors.description && <ErrorText>{errors.description}</ErrorText>}
            </InputBlock>

            <InputBlock>
              <Label>Website (Optional)</Label>
              <InputField
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourfield.com"
              />
            </InputBlock>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Creating account...' : 'Register Field'}
            </SubmitButton>
          </form>

          <BottomLink>
            Already have an account? <Link to="/login?role=field">Login here</Link>
          </BottomLink>
        </AccessFrame>
      </CenterBlock>

      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
    </Background>
  );
};

export default RegisterFieldPage;
